import { useEffect, useState } from "react";
import type { ResultCategory } from "@hearing/shared";
import { Layout } from "../components/Layout.js";
import { Button } from "../components/Button.js";
import { useScreener } from "../state.js";
import { bookingFallback } from "../api.js";

const COPY: Record<
  ResultCategory,
  { label: string; headline: string; body: string }
> = {
  good: {
    label: "Good",
    headline: "Your result is within the typical range",
    body: "You heard tones across the pitches we tested well. If you still notice difficulty hearing in some situations, a professional check can give you peace of mind.",
  },
  fair: {
    label: "Fair",
    headline: "Your hearing result is fair",
    body: "You showed some difficulty hearing quieter tones at a few pitches. A professional hearing check can help you understand whether any support would be useful.",
  },
  poor: {
    label: "Poor",
    headline: "Your result suggests hearing difficulty",
    body: "You had difficulty hearing quieter tones across several pitches. We recommend booking a comprehensive hearing assessment with an audiologist.",
  },
  severe: {
    label: "Severe",
    headline: "A professional hearing assessment is strongly recommended",
    body: "You had significant difficulty hearing quieter tones across most of the pitches tested. Please arrange a comprehensive assessment with an audiologist.",
  },
};

export function Results() {
  const { result, config, patient } = useScreener();
  const bookingUrl = config?.bookingUrl ?? bookingFallback;
  // Brief "analyzing" moment before the result is revealed.
  const [revealing, setRevealing] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setRevealing(false), 1600);
    return () => clearTimeout(t);
  }, []);

  if (revealing && result) {
    return (
      <Layout step="results">
        <div className="loading-state" role="status">
          <div className="results-loading" aria-hidden>
            <span className="tone-ring r1" />
            <span className="tone-ring r2" />
            <span className="results-loading-core" />
          </div>
          <h2>Reviewing your hearing check</h2>
          <p className="muted">Comparing the tones you heard in each ear…</p>
        </div>
      </Layout>
    );
  }

  if (!result) {
    return (
      <Layout step="results">
        <div className="empty-state">
          <h2>Your hearing check is complete</h2>
          <p className="muted screen-copy">
            Thank you for completing the check. For a full assessment, book an
            appointment with our audiology team.
          </p>
          {bookingUrl ? (
            <div className="actions center-actions">
              <Button onClick={() => openBooking(bookingUrl)}>
                Book an appointment
              </Button>
            </div>
          ) : null}
        </div>
      </Layout>
    );
  }

  const copy = COPY[result.category];
  const firstName = patient.name?.split(" ")[0];

  return (
    <Layout step="results">
      <div className="result-hero">
        <h2>
          {firstName ? `${firstName}, here's your result` : "Your result"}
        </h2>
        <div className={`result-badge ${result.category}`}>
          <span className="result-badge-icon" aria-hidden>
            {result.category === "good" ? "✓" : "!"}
          </span>
          {copy.label}
        </div>
      </div>

      <div className="result-interpretation">
        <h3>{copy.headline}</h3>
        <p className="muted">{copy.body}</p>
      </div>

      {bookingUrl ? (
        <div className="actions center-actions">
          <Button onClick={() => openBooking(bookingUrl)}>
            Book an appointment
          </Button>
        </div>
      ) : null}

      <div className="note">
        This online check is a screening tool performed on your own device and
        does not replace a diagnostic hearing test by a qualified audiologist.
      </div>
    </Layout>
  );
}

function openBooking(url: string): void {
  // Break out of the iframe so booking opens in the top window.
  if (window.top && window.top !== window.self) {
    window.top.location.href = url;
  } else {
    window.location.href = url;
  }
}
